import React, { useEffect, useState } from "react";
import { Plus, Save, Trash2, Pencil, X, Upload } from "lucide-react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const OurPartnersForm = ({
  data = [],
  onRefetch,
  apiBase = `${API_BASE_URL}/about`,
}) => {
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    role: "",
    description: "",
    image: "",
  });

  // Load rows when parent sends new data
  useEffect(() => {
    if (Array.isArray(data)) {
      setRows(data);
    }
  }, [data]);

  const resetForm = () => {
    setFormData({
      name: "",
      role: "",
      description: "",
      image: "",
    });
    setEditingId(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Image upload for partner photo
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type.toLowerCase())) {
      alert("Please upload JPG or PNG only.");
      return;
    }

    const uploadData = new FormData();
    uploadData.append("media", file);

    try {
      const res = await axios.post(
        `${API_BASE_URL}/admin/upload-media`,
        uploadData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const url = res?.data?.data?.files?.[0]?.url;
      if (url) {
        setFormData((prev) => ({ ...prev, image: url }));
      }
    } catch (error) {
      console.error(error);
      alert("Image upload failed.");
    }
  };

  const handleRemoveImage = () => {
    setFormData((prev) => ({ ...prev, image: "" }));
  };

  const handleEdit = (item) => {
    setEditingId(item.partner_id);
    setFormData({
      name: item.name,
      role: item.role,
      description: item.description,
      image: item.image,
    });
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this partner?")) return;

    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${apiBase}/partners/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      onRefetch?.();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete. Check console for details.");
    }
  };

   const handleSubmit = async (e) => {
  e.preventDefault();

  const payload = { ...formData };

  if (!payload.name || !payload.role || !payload.description) {
    alert("All fields except image are required.");
    return;
  }

  const token = localStorage.getItem("token");

  try {
    if (editingId) {
      // ✅ FIX: Include partner_id in payload and use correct endpoint
      payload.partner_id = editingId;
      await axios.put(`${apiBase}/partners/${editingId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("✅ Partner updated successfully!"); // ✅ Success message
    } else {
      // ✅ FIX: POST endpoint is correct
      await axios.post(`${apiBase}/partners`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("✅ Partner saved successfully!"); // ✅ Success message
    }

    resetForm();
    setFormOpen(false);
    onRefetch?.();
  } catch (err) {
    console.error("Save failed:", err);
    console.error("Error details:", err.response?.data);
    alert("❌ Failed to save partner. " + (err.response?.data?.message || ""));
  }
};

  return (
    <section className="bg-white rounded-xl shadow-md p-6 mb-10 w-full">
      <div className="max-w-6xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-blue-700">
              Our Partners
            </h2>
            <p className="text-sm text-gray-500">
              Manage partner profiles displayed on About page.
            </p>
          </div>
          <button
            onClick={() => {
              if (formOpen) resetForm();
              setFormOpen((p) => !p);
            }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            <Plus size={16} />
            {formOpen ? "Close" : "Add Partner"}
          </button>
        </div>

        {/* Partners Table */}
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-4 py-3 text-left">Photo</th>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Description</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr key={item.partner_id} className="border-t">
                  <td className="px-4 py-3">
                    {item.image ? (
                      <img
                        src={item.image}
                        className="h-12 w-12 rounded-full object-cover"
                        alt=""
                      />
                    ) : (
                      <span className="text-gray-400 italic">No Image</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{item.name}</td>
                  <td className="px-4 py-3">{item.role}</td>
                  <td className="px-4 py-3 max-w-[260px] truncate">
                    {item.description}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-3">
                      <button
                        onClick={() => handleEdit(item)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(item.partner_id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center py-6 text-gray-500 italic">
                    No partners added yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Form */}
        {formOpen && (
          <form
            onSubmit={handleSubmit}
            className="border-t pt-6 mt-4 space-y-6"
          >
            <h3 className="text-lg font-semibold text-gray-800">
              {editingId ? "Edit Partner" : "Add Partner"}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium">Name</label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full border rounded-lg px-3 py-2 shadow-sm"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium">Role</label>
                <input
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  className="w-full border rounded-lg px-3 py-2 shadow-sm"
                />
              </div>

              <div className="col-span-1 md:col-span-2 space-y-2">
                <label className="block text-sm font-medium">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  required
                  className="w-full border rounded-lg px-3 py-2 shadow-sm"
                ></textarea>
              </div>

              {/* Image Upload */}
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Partner Photo
                </label>

                {formData.image ? (
                  <div className="relative w-full max-w-xs">
                    <img
                      src={formData.image}
                      alt=""
                      className="w-full h-40 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      id="partner-image-upload"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <label
                      htmlFor="partner-image-upload"
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <Upload size={40} className="text-gray-400" />
                      <span className="text-gray-700">
                        Click to upload
                      </span>
                      <span className="text-xs text-gray-400">
                       Recommended Image Size: 1200 × 800 px (3:2), JPG/PNG , upto 300 KB.
                      </span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end">
              <button
                type="submit"
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg"
              >
                <Save size={18} />
                {editingId ? "Update Partner" : "Save Partner"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default OurPartnersForm;
